import { useStore } from '../state/store';
import { Card, Chip, Icon, SectionHeader } from '../ui';
import { fmtCompact } from '../utils/format';

/**
 * Compact per-scenario comparison: rows are key milestones (5/10/20/retire),
 * columns are the active scenario plus every scenario in the compare set.
 * Deltas are relative to the active projection.
 */
export function ScenarioCompareTable() {
  const { activeScenario, projection, compareProjections, assumptions } = useStore();

  if (compareProjections.length === 0) return null;

  const horizon = projection.years.length;
  const retireIdx = Math.min(
    assumptions.targetRetirementAge - assumptions.currentAge,
    horizon - 1,
  );
  const milestones = [
    { label: '+5y', idx: Math.min(5, horizon - 1) },
    { label: '+10y', idx: Math.min(10, horizon - 1) },
    {
      label: `Retire (${assumptions.targetRetirementAge})`,
      idx: Math.max(0, retireIdx),
    },
    { label: 'End', idx: horizon - 1 },
  ];

  const all = [
    {
      id: activeScenario.id,
      name: activeScenario.name,
      icon: activeScenario.icon,
      color: activeScenario.color,
      years: projection.years,
      active: true,
    },
    ...compareProjections.map(c => ({
      id: c.scenario.id,
      name: c.scenario.name,
      icon: c.scenario.icon,
      color: c.scenario.color,
      years: c.projection.years,
      active: false,
    })),
  ];

  return (
    <Card flush>
      <SectionHeader
        title="Scenario compare"
        subtitle="Net worth at key milestones · active in bold"
        bordered
        muted
        right={<Chip tone="accent" size="sm">{compareProjections.length + 1} scenarios</Chip>}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-ink-50 text-left text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:bg-ink-800/50 dark:text-ink-300">
              <th className="px-3 py-2">Scenario</th>
              {milestones.map(m => (
                <th key={m.label} className="px-3 py-2 text-right whitespace-nowrap">
                  {m.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {all.map(row => {
              const active = row.active;
              const activeNWAtMilestone = (i: number) =>
                projection.years[i]?.netWorth ?? 0;
              return (
                <tr
                  key={row.id}
                  className={`border-t border-ink-100 dark:border-ink-800 ${
                    active ? 'bg-accent-50/40 dark:bg-accent-700/10' : ''
                  }`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm"
                        style={{ backgroundColor: `${row.color}22`, color: row.color }}
                        aria-hidden
                      >
                        {row.icon}
                      </span>
                      <span className={`truncate ${active ? 'font-bold' : 'font-semibold'}`}>
                        {row.name}
                      </span>
                      {active && <Icon name="sparkle" size={12} className="text-accent-600" />}
                    </div>
                  </td>
                  {milestones.map(m => {
                    const value = row.years[m.idx]?.netWorth ?? 0;
                    const delta = active ? 0 : value - activeNWAtMilestone(m.idx);
                    return (
                      <td
                        key={m.label}
                        className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap"
                      >
                        <div className={active ? 'font-bold' : ''}>
                          ${fmtCompact(value)}
                        </div>
                        {!active && Math.abs(delta) > 1_000 && (
                          <div
                            className={`text-[10px] font-semibold ${
                              delta >= 0
                                ? 'text-pos-700 dark:text-pos-500'
                                : 'text-neg-700 dark:text-neg-500'
                            }`}
                          >
                            {delta >= 0 ? '+' : '−'}${fmtCompact(Math.abs(delta))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
